import { FC } from 'react';
import { Container } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { generatePath, useParams } from 'react-router-dom';

import { Breadcrumbs } from '../../components/breadcrumbs/breadcrumbs';
import { PageTopBar } from '../../components/page-top-bar/page-top-bar';
import * as Routes from '../../constants/routes';
import { ImperativeModalProvider } from '../../context/imperative-modal/imperative-modal';
import { useGetDirectoryQuery } from '../../graphql/generated';
import { useEnableDynamicFilesPolling } from '../../hooks/use-enable-files-polling/use-enable-files-polling';
import {
  usePagination,
  usePaginationConfig,
} from '../../hooks/use-pagination/use-pagination';
import { FileBrowserContainer } from './file-browser-container';

export type FileBrowserPageParams = {
  cedantId: string;
  directoryId?: string;
};

export const navigateToDirectory = (
  params: FileBrowserPageParams,
  searchParams?: Record<string, string>
): string => {
  let path;
  if (params.directoryId) {
    path = generatePath(Routes.DIRECTORY_BROWSER, {
      cedantId: params.cedantId,
      directoryId: params.directoryId,
    });
  } else {
    path = generatePath(Routes.TREATY_BROWSER, {
      cedantId: params.cedantId,
    });
  }

  if (searchParams) {
    const searchParamsString = new URLSearchParams(searchParams).toString();

    return `${path}?${searchParamsString}`;
  }

  return path;
};

export const FileBrowserPage: FC = () => {
  const { cedantId, directoryId } = useParams() as FileBrowserPageParams;
  const {
    error,
    data,
    refetch,
    loading,
    startPolling,
    stopPolling,
  } = useGetDirectoryQuery({
    variables: {
      cedant: cedantId,
      directoryId,
      objectsPagination: usePaginationConfig(),
    },
    fetchPolicy: 'cache-and-network',
  });

  const { pausePolling, resumePolling } = useEnableDynamicFilesPolling({
    startPolling,
    stopPolling,
    data: data?.cedant?.directory.objects.objects,
  });

  const pagination = usePagination({
    files: data?.cedant?.directory.objects.objects || [],
    fileCount: data?.cedant?.directory.objects.count || 0,
  });

  const handleRefetchFiles = async () => {
    await refetch();
  };

  const pathId = data?.cedant?.directory.path;

  return (
    <>
      <Helmet>
        <title>Treaty Files</title>
      </Helmet>
      <PageTopBar />
      <Container
        display="flex"
        flex={1}
        flexDirection="column"
        p={8}
        maxW="100%"
      >
        {data?.cedant && (
          <Breadcrumbs
            cedantId={cedantId}
            breadcrumbs={data.cedant.directory.breadcrumbs}
          />
        )}
        <ImperativeModalProvider>
          <FileBrowserContainer
            error={error}
            pathId={pathId}
            loading={loading}
            pagination={pagination}
            cedantId={cedantId}
            directoryId={directoryId || data?.cedant?.directory.id}
            refetchFiles={handleRefetchFiles}
            // default to false if no API response to this grant
            canCreate={data?.cedant?.canCreate || false}
            pausePolling={pausePolling}
            resumePolling={resumePolling}
          />
        </ImperativeModalProvider>
      </Container>
    </>
  );
};
